import { Component, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import { BreadcrumbComponent } from '../../../../core/layout/components/breadcrumb/breadcrumb.component';
import { InfoTooltipComponent } from '../../../../shared/components/ui/info-tooltip/info-tooltip.component';
import { ApiErrorResponse } from '../../../../shared/models/api-error-response.interface';
import { ApiCustomerSearchErrorCode, ApiCustomerSearchService, CustomerSearchItem, PredefinedValuesResponse } from '../../../../shared/services/api-customer-search.service';
import { OrganizationStoreService } from '../../../../shared/services/organization-store.service';
import { CustomerDetailsDialogComponent } from '../../components/customer-details-dialog/customer-details-dialog.component';

type SearchField = keyof CustomerSearchItem;
type SortDirection = 'asc' | 'desc';

interface RangeField {
  // The result field this range stands in for — used only for the label; the actual criteria
  // keys sent are `${key}From` / `${key}To`, not `key` itself.
  key: string;
  label: string;
}

@Component({
  selector: 'app-customer-search',
  imports: [CommonModule, FormsModule, BreadcrumbComponent, InfoTooltipComponent, CustomerDetailsDialogComponent],
  templateUrl: './customer-search.component.html',
  styleUrls: ['./customer-search.component.css']
})
export class CustomerSearchComponent {
  // Every field the API returns is also displayable — drives the results table columns.
  readonly searchFields: SearchField[] = [
    'createdOn', 'lastUpdatedOn', 'eventDate', 'expiryDate', 'surveyRequestId', 'vin',
    'externalRecordId', 'segmentId', 'businessCode', 'businessName', 'country', 'alertRaised',
    'campaignName', 'surveyId', 'recordDiscardReason', 'customerName', 'firstName', 'lastName',
    'emailAddress', 'emailDiscardReason', 'emailDeliveryDate', 'mobilePhone', 'mobileDiscardReason',
    'mobileDeliveryDate', 'fileName', 'responseChannel', 'responseDate', 'shortCode', 'surveyLink'
  ];

  // The only fields the search form exposes — a deliberately narrower set than searchFields.
  readonly criteriaFields: SearchField[] = [
    'surveyRequestId', 'externalRecordId', 'emailAddress', 'mobilePhone', 'shortCode', 'vin',
    'surveyId', 'country', 'segmentId', 'businessCode', 'campaignName', 'customerName',
    'recordDiscardReason', 'emailDiscardReason', 'mobileDiscardReason', 'alertRaised', 'fileName',
    'responseChannel'
  ];

  // Matched as a substring ("contains" / LIKE) on the backend rather than an exact value.
  readonly likeFields: SearchField[] = [
    'vin', 'emailAddress', 'mobilePhone', 'fileName', 'businessCode', 'campaignName', 'customerName'
  ];

  // NULL-aware on the backend: an empty/whitespace string matches records where the column is
  // NULL or empty, so "" can't double as "don't filter" for these. They're selects fed by the
  // predefined-values API: "Any" stays "" and is omitted like any blank criterion, while the
  // "Null" option carries NULL_OPTION and is translated back to "" when the request is built.
  readonly nullAwareFields: (keyof PredefinedValuesResponse & SearchField)[] = [
    'responseChannel', 'recordDiscardReason', 'emailDiscardReason', 'mobileDiscardReason'
  ];
  readonly NULL_OPTION = '__null__';
  predefinedValues = signal<Partial<PredefinedValuesResponse>>({});
  loadingPredefinedValues = signal(true);

  // Searched as a from/to range instead of a single exact value. These are excluded from the
  // single-value form fields above, but the result table still shows the real createdOn/
  // responseDate columns from the response untouched.
  readonly rangeFields: RangeField[] = [
    { key: 'createdOn', label: 'Created On' },
    { key: 'responseDate', label: 'Response Date' }
  ];

  // The API apparently expects full timestamps, not bare dates — a plain YYYY-MM-DD alone
  // returned no results. The form only collects a date (no time picker), so pad it out to
  // midnight ourselves before sending, rather than asking the user to enter a time they don't need.
  private readonly dateCriteriaKeys: string[] = this.rangeFields.flatMap(rf => [`${rf.key}From`, `${rf.key}To`]);

  organizationId: string;
  criteria: Record<string, string> = {};
  // Server-side ordering of the search results — separate from the client-side column sort below,
  // which only reorders the current page.
  searchSortDirection: SortDirection = 'desc';

  loading = signal(false);
  error = signal<string | null>(null);
  hasSearched = signal(false);
  items = signal<CustomerSearchItem[]>([]);
  totalCount = signal(0);
  currentPage = signal(1);
  perPage = 25;
  readonly perPageOptions = [10, 25, 50, 100];

  sortField = signal<SearchField | null>(null);
  sortDirection = signal<SortDirection>('asc');

  // Survey request whose details popup is open, if any.
  detailsSurveyRequestId = signal<string | null>(null);

  // Only rows with a real surveyRequestId can be looked up — null, empty and 0 are all "none".
  hasSurveyRequestId(item: CustomerSearchItem): boolean {
    const id = String(item.surveyRequestId ?? '').trim();
    return id !== '' && Number(id) !== 0;
  }

  get hasClickableRows(): boolean {
    return this.items().some(item => this.hasSurveyRequestId(item));
  }

  openDetails(item: CustomerSearchItem): void {
    if (this.hasSurveyRequestId(item)) this.detailsSurveyRequestId.set(String(item.surveyRequestId).trim());
  }

  constructor(
    public organizationStore: OrganizationStoreService,
    private customerSearchService: ApiCustomerSearchService) {
    this.organizationId = this.organizationStore.selectedOrganizationId() ?? '';
    this.loadPredefinedValues();
  }

  isNullAware(field: SearchField): boolean {
    return (this.nullAwareFields as SearchField[]).includes(field);
  }

  // The non-empty predefined values for a field; the "" entry is rendered as the Null option instead.
  predefinedOptions(field: SearchField): string[] {
    const values = this.predefinedValues()[field as keyof PredefinedValuesResponse] ?? [];
    return values.filter(v => v.trim().length > 0);
  }

  hasNullOption(field: SearchField): boolean {
    const values = this.predefinedValues()[field as keyof PredefinedValuesResponse] ?? [];
    return values.some(v => v.trim().length === 0);
  }

  private loadPredefinedValues(): void {
    this.customerSearchService.predefinedValues().subscribe({
      next: (result) => {
        this.predefinedValues.set(result);
        this.loadingPredefinedValues.set(false);
      },
      // Still show the form on failure — the NULL-aware selects just fall back to "Any" only.
      error: (err) => {
        this.error.set(this.formatApiError(err, 'Failed to load predefined values.'));
        this.loadingPredefinedValues.set(false);
      }
    });
  }

  get totalPages(): number {
    return Math.ceil(this.totalCount() / this.perPage) || 1;
  }

  canSearch(): boolean {
    return !!this.organizationId && !this.loading();
  }

  get surveys() {
    return this.organizationStore.organizations().find(o => o.id === this.organizationId)?.surveys ?? [];
  }

  get segments(): string[] {
    return this.organizationStore.organizations().find(o => o.id === this.organizationId)?.segments ?? [];
  }

  onOrganizationChange(): void {
    // The previously selected survey/segment almost certainly doesn't belong to the new organization.
    this.criteria['surveyId'] = '';
    this.criteria['segmentId'] = '';
  }

  formatFieldLabel(field: string): string {
    return field.replace(/([a-z0-9])([A-Z])/g, '$1 $2').replace(/^./, c => c.toUpperCase());
  }

  getFieldValue(item: CustomerSearchItem, field: SearchField): string | boolean {
    return item[field];
  }

  // ── Sorting ──────────────────────────────────────────────────────────────

  sortBy(field: SearchField): void {
    if (this.sortField() === field) {
      this.sortDirection.set(this.sortDirection() === 'asc' ? 'desc' : 'asc');
    } else {
      this.sortField.set(field);
      this.sortDirection.set('asc');
    }
  }

  sortIndicator(field: SearchField): string {
    if (this.sortField() !== field) return '';
    return this.sortDirection() === 'asc' ? '▲' : '▼';
  }

  get sortedItems(): CustomerSearchItem[] {
    const field = this.sortField();
    if (!field) return this.items();

    const dir = this.sortDirection() === 'asc' ? 1 : -1;
    return [...this.items()].sort((a, b) => {
      const av = a[field];
      const bv = b[field];
      if (av === bv) return 0;
      if (av === '' || av === null || av === undefined) return 1;
      if (bv === '' || bv === null || bv === undefined) return -1;
      return (av < bv ? -1 : 1) * dir;
    });
  }

  // ── Search ───────────────────────────────────────────────────────────────

  search(): void {
    if (!this.canSearch()) return;
    this.currentPage.set(1);
    this.runSearch();
  }

  clear(): void {
    this.criteria = {};
    this.searchSortDirection = 'desc';
    this.items.set([]);
    this.hasSearched.set(false);
    this.error.set(null);
  }

  onPerPageChange(): void {
    this.currentPage.set(1);
    this.runSearch();
  }

  changePage(page: number): void {
    if (page < 1 || page > this.totalPages || page === this.currentPage()) return;
    this.currentPage.set(page);
    this.runSearch();
  }

  private runSearch(): void {
    if (!this.organizationId) return;

    this.loading.set(true);
    this.error.set(null);
    this.hasSearched.set(true);

    const trimmedCriteria: Record<string, string> = {};
    for (const [key, value] of Object.entries(this.criteria)) {
      if (value === this.NULL_OPTION) {
        trimmedCriteria[key] = '';
        continue;
      }
      if (!value || value.trim().length === 0) continue;
      const trimmed = value.trim();
      trimmedCriteria[key] = this.dateCriteriaKeys.includes(key) ? `${trimmed}T00:00:00` : trimmed;
    }

    this.customerSearchService.searchCustomers(this.organizationId, trimmedCriteria, this.currentPage(), this.perPage, this.searchSortDirection).subscribe({
      next: (result) => {
        this.items.set(result.items);
        this.totalCount.set(result.totalCount);
        this.loading.set(false);
      },
      error: (err) => {
        this.error.set(this.formatApiError(err, 'Failed to search customers. Please try again.'));
        this.loading.set(false);
      }
    });
  }

  private formatApiError(err: unknown, fallback: string): string {
    if (err instanceof HttpErrorResponse) {
      if (err.status === 401) {
        return 'Unauthorized. Please sign in again.';
      }
      const apiError = err.error as ApiErrorResponse<ApiCustomerSearchErrorCode> | undefined;
      if (apiError?.code && apiError?.description) {
        const details = apiError.details?.length
          ? ` (${apiError.details.map(d => typeof d === 'string' ? d : JSON.stringify(d)).join('; ')})`
          : '';
        return `[${apiError.code}] ${apiError.description}${details}`;
      }
      return `${fallback} | ${err.message}`;
    }
    return `${fallback} | ${err}`;
  }
}
